import type { TimeTrackTarget } from "./timetrack";

const ASSIGNED_RECENCY_BONUS = 3 * 24 * 60 * 60 * 1000;
function timestamp(value?: string | null): number {
  const parsed = value ? Date.parse(value) : NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Pin the most recently tracked project. For the rest, personal activity wins
 * over no activity; assignment gives a three-day recency advantage, not an
 * unlimited boost that could bury a project used today beneath an old assignment.
 * Untouched projects: assigned first, then newest creation date. Stable ties.
 * This function never mutates the authorized input or invents access.
 */
export function rankTimeTrackProjects(targets: TimeTrackTarget[]): TimeTrackTarget[] {
  const projects = targets.filter(t => t.targetType === "project");
  const latestTracked = Math.max(0, ...projects.map(p => timestamp(p.lastTrackedAt)));
  const activity = (p: TimeTrackTarget) => Math.max(timestamp(p.lastInteractedAt), timestamp(p.lastTrackedAt));
  return [...projects].sort((a, b) => {
    const pinned = (p: TimeTrackTarget) => latestTracked > 0 && timestamp(p.lastTrackedAt) === latestTracked ? 1 : 0;
    if (pinned(a) !== pinned(b)) return pinned(b) - pinned(a);
    const aTime = activity(a), bTime = activity(b);
    if (!!aTime !== !!bTime) return bTime ? 1 : -1;
    if (aTime && bTime) {
      const difference = bTime + (b.assignedToMe ? ASSIGNED_RECENCY_BONUS : 0)
        - aTime - (a.assignedToMe ? ASSIGNED_RECENCY_BONUS : 0);
      if (difference) return difference;
    }
    return Number(!!b.assignedToMe) - Number(!!a.assignedToMe)
      || timestamp(b.createdAt) - timestamp(a.createdAt)
      || a.label.localeCompare(b.label)
      || a.projectId.localeCompare(b.projectId);
  });
}
